import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { message } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { MfaCodeInput } from '@/components/mfa/MfaCodeInput'
import { MfaRecoveryKeyInput } from '@/components/mfa/MfaRecoveryKeyInput'
import { MfaProofType } from '@/consts/mfa'
import { MfaService } from '@/services/mfa-service'
import { getErrorCodeMessage } from '@/utils/error'

interface MfaDeactivateModalProps {
  onClose: () => void
  onSuccess: () => void
}

type DeactivateMode = 'totp' | 'recovery'

const TOTP_CODE_LENGTH = 6
const RECOVERY_KEY_LENGTH = 8

// Mirrors the login MfaChallengeModal flow: a 6-digit TOTP screen with a
// "Disable via Recovery Key" link that switches to the recovery-key screen.
export function MfaDeactivateModal({ onClose, onSuccess }: MfaDeactivateModalProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<DeactivateMode>('totp')
  const [totpCode, setTotpCode] = useState('')
  const [recoveryKey, setRecoveryKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const isRecovery = mode === 'recovery'

  const handleDisable = async (viaRecovery: boolean, submittedProof?: string) => {
    const proof = submittedProof ?? (viaRecovery ? recoveryKey : totpCode)
    const requiredLength = viaRecovery ? RECOVERY_KEY_LENGTH : TOTP_CODE_LENGTH
    if (proof.length !== requiredLength || loading) {
      return
    }

    setLoading(true)
    setError('')
    try {
      const result = viaRecovery
        ? await MfaService.disable(MfaProofType.Recovery, proof)
        : await MfaService.disable(MfaProofType.Totp, proof)

      if (result.code === 0) {
        message.success(t('mfa.deactivate.success'))
        onSuccess()
        return
      }

      const errorMessage = getErrorCodeMessage(result.code, t)
      if (viaRecovery) {
        setRecoveryKey('')
      } else {
        setTotpCode('')
      }
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const switchToRecovery = () => {
    setError('')
    setTotpCode('')
    setMode('recovery')
  }

  // Back on the recovery screen returns to the TOTP screen, not out of the flow.
  const backToTotp = () => {
    setError('')
    setRecoveryKey('')
    setMode('totp')
  }

  return (
    <Modal className="w-[480px]" open={true} onClose={onClose}>
      <Modal.Header>
        {isRecovery ? t('mfa.challenge.recoveryTitle') : t('mfa.deactivate.verifyTitle')}
      </Modal.Header>
      <Modal.Body>
        {isRecovery ? (
          <>
            <p className="t-sm text-secondary mb-6">{t('mfa.challenge.recoveryDescription')}</p>
            <MfaRecoveryKeyInput
              value={recoveryKey}
              onChange={(value) => {
                setRecoveryKey(value)
                if (error) {
                  setError('')
                }
              }}
              onComplete={(value) => handleDisable(true, value)}
              disabled={loading}
              hasError={!!error}
              autoFocus
            />
          </>
        ) : (
          <>
            <p className="t-sm text-secondary mb-6">{t('mfa.challenge.description')}</p>
            <MfaCodeInput
              value={totpCode}
              onChange={(value) => {
                setTotpCode(value)
                if (error) {
                  setError('')
                }
              }}
              onComplete={(value) => handleDisable(false, value)}
              disabled={loading}
              hasError={!!error}
              autoFocus
            />
          </>
        )}
        {error && <p className="mt-3 t-sm text-error">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        {isRecovery ? (
          <div className="flex w-full items-center justify-end gap-3">
            <Button type="outline" onClick={backToTotp} disabled={loading} className="min-w-[88px]">
              {t('mfa.challenge.back')}
            </Button>
            <Button
              type="danger"
              onClick={() => handleDisable(true)}
              disabled={loading || recoveryKey.length !== RECOVERY_KEY_LENGTH}
              className="min-w-[120px]"
            >
              {t('mfa.challenge.disableMfa')}
            </Button>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between gap-3">
            <button
              type="button"
              className="t-sm text-btn-secondary disabled:opacity-60"
              onClick={switchToRecovery}
              disabled={loading}
            >
              {t('mfa.challenge.disableViaRecovery')}
            </button>
            <div className="flex items-center gap-3">
              <Button type="outline" onClick={onClose} disabled={loading} className="min-w-[88px]">
                {t('common.confirmModalCancel')}
              </Button>
              <Button
                type="danger"
                onClick={() => handleDisable(false)}
                disabled={loading || totpCode.length !== TOTP_CODE_LENGTH}
                className="min-w-[120px]"
              >
                {t('mfa.challenge.disableMfa')}
              </Button>
            </div>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  )
}
